/**
 * The translate icon's popover (F-70 + "translate this cell", 2026-09-04): a
 * TWO-choice menu, in the shape of a system menu rather than a form —
 *
 *   ┌────────────────────────────────────────────┐
 *   │ ⤵  Translate this cell        Into Korean ▾ │
 *   │    Adds the translation under the text       │
 *   ├────────────────────────────────────────────┤
 *   │ ▥  Translate whole column…                 › │
 *   │    Original Script → a column, row by row    │
 *   └────────────────────────────────────────────┘
 *
 * The first row is the immediate gesture: the selected cells go in one
 * request and each answer is appended under its cell's own text
 * (cell-translate.ts). The language chip beside it is the one control the
 * gesture needs, remembered across sessions (script/translate-pick.ts — the
 * same "my language" the brainstorm panel uses, on purpose). The second row
 * is the existing column job: it opens the F-70 dialog.
 *
 * Both rows always show, each greyed with its reason when it does not apply
 * (a multi-column selection cannot be a column target; an image cell has no
 * prose) — a menu whose rows appear and disappear with the selection would
 * be a menu nobody could learn.
 *
 * Why the language chip is its own little list and not a <select>: the
 * toolbar keeps keyboard focus on the grid by cancelling mousedown on
 * everything but text inputs (SelectionToolbar), and a native select cannot
 * open without focus. Buttons can.
 */
import { isBgmCellValue, isImageCellValue } from "@kola/shared";
import { useEffect, useMemo, useState } from "react";
import { useStore } from "zustand";

import { translateCells } from "../../core/api.js";
import type { BoardSession } from "../../core/session.js";
import type {
  CellsToolbarSelection,
  ToolbarActionContext,
} from "../../registries/toolbar-actions/index.js";
import { showToast } from "../../ui/toast.js";
import { rememberTranslateLanguage, storedTranslateLanguage } from "../script/translate-pick.js";
import {
  appendTranslation,
  cellIneligibleMessage,
  cellTranslateEligibility,
  cellTranslateSummary,
  cellTranslateTitle,
  translatableCells,
  type CellTranslateOutcome,
  type TranslatableCell,
} from "./cell-translate.js";
import { translateErrorMessage } from "./dialog-state.js";
import { translateEligibility, type TranslateColumnFacts } from "./eligibility.js";
import { isJobRunning } from "./job-view.js";
import { languageLabel, TRANSLATE_LANGUAGES } from "./languages.js";
import { translateUi } from "./store.js";
import { useTranslationJob } from "./use-job.js";

/* ------------------------------------------------------------------ */
/* Icons (16 px inline SVG, stroke = currentColor)                     */
/* ------------------------------------------------------------------ */

const svgProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 16 16",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

/** A cell with a line of text and a second line arriving under it. */
const iconCell = (
  <svg {...svgProps} aria-hidden="true">
    <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
    <path d="M4.5 6h7" />
    <path d="M4.5 9.5h4.5" strokeDasharray="1.5 1.5" />
  </svg>
);

/** A column standing out of a grid. */
const iconColumn = (
  <svg {...svgProps} aria-hidden="true">
    <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
    <path d="M6.5 2.5v11M9.5 2.5v11" opacity="0.45" />
    <rect x="6.5" y="2.5" width="3" height="11" fill="currentColor" stroke="none" opacity="0.28" />
  </svg>
);

const iconChevron = (
  <svg {...svgProps} aria-hidden="true">
    <path d="m6 4 4 4-4 4" />
  </svg>
);

const iconCheck = (
  <svg {...svgProps} aria-hidden="true">
    <path d="m3.5 8.5 3 3 6-7" />
  </svg>
);

/* ------------------------------------------------------------------ */
/* The cell request                                                    */
/* ------------------------------------------------------------------ */

/** Cells of the selection that hold prose, read live from the doc. */
function proseCells(session: BoardSession, sel: CellsToolbarSelection): TranslatableCell[] {
  const { board } = session;
  return translatableCells(
    sel,
    (rowId, colId) => board.getCellText(sel.sheetId, rowId, colId),
    (rowId, colId) => {
      const raw = board.getCell(sel.sheetId, rowId, colId)?.v;
      return !isImageCellValue(raw) && !isBgmCellValue(raw);
    },
  );
}

/**
 * Send the cells, then write each answer under its cell — ONE transaction,
 * so the whole gesture is one undo step. Each write re-reads the cell first
 * (docs/07 C4): a cell whose text changed while the request was out is left
 * alone and counted, never overwritten with a translation of old text.
 */
async function runCellTranslate(
  session: BoardSession,
  sel: CellsToolbarSelection,
  cells: TranslatableCell[],
  to: string,
): Promise<void> {
  const ui = translateUi.getState();
  ui.setCellRun({ boardId: session.boardId, sheetId: sel.sheetId, count: cells.length });
  try {
    const result = await translateCells(session.boardId, {
      to,
      cells: cells.map((cell) => ({ id: cell.key, text: cell.text })),
    });
    const outcome: CellTranslateOutcome = { written: 0, unchanged: 0, skipped: 0 };
    session.batch(() => {
      for (const cell of cells) {
        const translation = result.translations[cell.key];
        if (translation === undefined) continue; // the server guarantees every key; belt and braces
        const current = session.board.getCellText(sel.sheetId, cell.rowId, cell.colId);
        if (current !== cell.text) {
          outcome.skipped += 1;
          continue;
        }
        const next = appendTranslation(current, translation);
        if (next === current) {
          outcome.unchanged += 1;
          continue;
        }
        session.setCellValue(sel.sheetId, cell.rowId, cell.colId, next);
        outcome.written += 1;
      }
    });
    showToast(cellTranslateSummary(outcome, languageLabel(to)));
  } catch (error) {
    showToast(translateErrorMessage(error));
  } finally {
    translateUi.getState().setCellRun(null);
  }
}

/* ------------------------------------------------------------------ */
/* The menu                                                            */
/* ------------------------------------------------------------------ */

function factsFor(session: BoardSession, sheetId: string): TranslateColumnFacts {
  return {
    sourceColId: session.board.sourceColumnId(sheetId),
    isLocked: (colId) => session.board.isColumnLocked(sheetId, colId),
    hasColumn: (colId) => session.board.hasColumn(sheetId, colId),
  };
}

/** Is the selection exactly the F-28 source column? */
export function isSourceSelection(ctx: ToolbarActionContext): boolean {
  const sel = ctx.selection;
  if (sel.kind !== "cells" || sel.colIds.length !== 1) return false;
  const colId = sel.colIds[0];
  return colId !== undefined && colId === ctx.session.board.sourceColumnId(sel.sheetId);
}

/** May the column job be offered for this selection? */
export function columnTranslatable(ctx: ToolbarActionContext): boolean {
  const sel = ctx.selection;
  if (sel.kind !== "cells") return false;
  return isSourceSelection(ctx) || translateEligibility(sel, factsFor(ctx.session, sel.sheetId)).ok;
}

/** May the cell request be offered — is there any prose in the selection? */
export function cellsTranslatable(ctx: ToolbarActionContext): boolean {
  const sel = ctx.selection;
  if (sel.kind !== "cells") return false;
  return proseCells(ctx.session, sel).length > 0;
}

export function TranslateMenu({ ctx }: { ctx: ToolbarActionContext }) {
  const sel = ctx.selection;
  const cellRun = useStore(translateUi, (s) => s.cellRun);
  const job = useTranslationJob(ctx.session);
  const [to, setTo] = useState<string>(() => storedTranslateLanguage());
  const [choosingLanguage, setChoosingLanguage] = useState(false);

  // Read once per open, like the dialog's mapping: the menu is short-lived
  // (it closes when the selection changes), and the cells it names are the
  // cells the user is looking at.
  const cells = useMemo(
    () => (sel.kind === "cells" ? proseCells(ctx.session, sel) : []),
    [ctx.session, sel],
  );
  const eligibility = cellTranslateEligibility(cells);
  const runningHere = cellRun !== null && cellRun.boardId === ctx.session.boardId;
  const columnOk = columnTranslatable(ctx);
  const columnBusy = isJobRunning(job);

  // Closing the chip's list with Esc must not also close the popover — the
  // toolbar's own Esc handler runs on window capture, so this one stops it.
  useEffect(() => {
    if (!choosingLanguage) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      event.preventDefault();
      setChoosingLanguage(false);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [choosingLanguage]);

  if (sel.kind !== "cells") return null;

  const pickLanguage = (tag: string) => {
    setTo(tag);
    rememberTranslateLanguage(tag);
    setChoosingLanguage(false);
  };

  const translateHere = () => {
    if (!eligibility.ok || runningHere) return;
    ctx.closePopover();
    void runCellTranslate(ctx.session, sel, eligibility.cells, to);
  };

  const openColumnDialog = () => {
    if (!columnOk) return;
    const colId = sel.colIds[0];
    if (colId === undefined) return;
    ctx.closePopover();
    translateUi.getState().openDialog({
      boardId: ctx.session.boardId,
      sheetId: sel.sheetId,
      colId: isSourceSelection(ctx) ? null : colId,
    });
  };

  const cellTitle = runningHere
    ? `Translating ${cellRun.count === 1 ? "this cell" : `${cellRun.count} cells`}…`
    : cellTranslateTitle(cells.length);
  const cellSubtitle = eligibility.ok
    ? "Adds the translation under the text"
    : cellIneligibleMessage(eligibility);

  return (
    <div className="tx-choice" role="menu" aria-label="Translate">
      <div className={`tx-choice-row${eligibility.ok && !runningHere ? "" : " disabled"}`}>
        <button
          type="button"
          role="menuitem"
          className="tx-choice-main"
          disabled={!eligibility.ok || runningHere}
          onClick={translateHere}
        >
          <span className="tx-choice-icon">
            {runningHere ? <span className="st-spinner" aria-hidden="true" /> : iconCell}
          </span>
          <span className="tx-choice-text">
            <span className="tx-choice-title">{cellTitle}</span>
            <span className="tx-choice-sub">{cellSubtitle}</span>
          </span>
        </button>
        <button
          type="button"
          className={`tx-lang-chip${choosingLanguage ? " open" : ""}`}
          aria-haspopup="listbox"
          aria-expanded={choosingLanguage}
          aria-label={`Into ${languageLabel(to)}`}
          disabled={runningHere}
          onClick={() => setChoosingLanguage((open) => !open)}
        >
          <span className="tx-lang-chip-label">Into</span>
          <span className="tx-lang-chip-value">{languageLabel(to)}</span>
          <span className="tx-lang-chip-caret" aria-hidden="true">
            ▾
          </span>
        </button>
      </div>

      {choosingLanguage && (
        <ul className="tx-lang-list" role="listbox" aria-label="Translate into">
          {TRANSLATE_LANGUAGES.map((language) => (
            <li key={language.tag} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={language.tag === to}
                className={`tx-lang-option${language.tag === to ? " selected" : ""}`}
                onClick={() => pickLanguage(language.tag)}
              >
                <span className="tx-lang-check">{language.tag === to ? iconCheck : null}</span>
                {language.label}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="tx-choice-sep" role="separator" />

      <div className={`tx-choice-row${columnOk && !columnBusy ? "" : " disabled"}`}>
        <button
          type="button"
          role="menuitem"
          className="tx-choice-main"
          disabled={!columnOk || columnBusy}
          onClick={openColumnDialog}
        >
          <span className="tx-choice-icon">{iconColumn}</span>
          <span className="tx-choice-text">
            <span className="tx-choice-title">Translate whole column…</span>
            <span className="tx-choice-sub">
              {columnBusy
                ? "A column is being translated — wait for it to finish"
                : columnOk
                  ? isSourceSelection(ctx)
                    ? "Original Script → a column, row by row"
                    : "Original Script → this column, row by row"
                  : "Select one column to translate into"}
            </span>
          </span>
          <span className="tx-choice-chevron" aria-hidden="true">
            {iconChevron}
          </span>
        </button>
      </div>
    </div>
  );
}
